import { useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { VAPID_PUBLIC_KEY } from '../../graphql/queries'
import { REGISTER_PUSH_SUBSCRIPTION } from '../../graphql/mutations'
import {
  enablePushNotifications,
  isInstalledApp,
  isPushSupported
} from '../../utils/pushNotifications'

const ASKED_KEY = 'svirrens_push_auto_asked'

/**
 * Når appen er hentet til hjemmeskærmen, slår vi notifikationer til.
 * Telefonen viser stadig sin egen tilladelsesdialog én gang.
 */
export default function AutoEnablePush() {
  const installed = isInstalledApp()
  const { data } = useQuery(VAPID_PUBLIC_KEY, {
    skip: !installed || !isPushSupported(),
    fetchPolicy: 'cache-first'
  })
  const vapidKey = data?.vapidPublicKey
  const [registerPush] = useMutation(REGISTER_PUSH_SUBSCRIPTION)

  useEffect(() => {
    if (!installed || !vapidKey || !isPushSupported()) return undefined
    if (typeof Notification === 'undefined' || Notification.permission === 'denied') return undefined

    let cancelled = false
    let removeTap = () => {}

    const subscribe = async () => {
      const json = await enablePushNotifications(vapidKey)
      if (cancelled || !json?.endpoint || !json.keys) return false
      await registerPush({
        variables: {
          subscription: {
            endpoint: json.endpoint,
            expirationTime: json.expirationTime != null ? String(json.expirationTime) : null,
            keys: {
              p256dh: json.keys.p256dh,
              auth: json.keys.auth
            }
          }
        }
      })
      return true
    }

    const start = async () => {
      if (Notification.permission === 'granted') {
        await subscribe()
        return
      }

      try {
        if (localStorage.getItem(ASKED_KEY) === '1') return
      } catch {
        /* ignore */
      }

      const ok = await subscribe()
      if (cancelled) return

      if (ok || Notification.permission === 'denied') {
        try { localStorage.setItem(ASKED_KEY, '1') } catch { /* ignore */ }
        return
      }

      const onTap = () => {
        try { localStorage.setItem(ASKED_KEY, '1') } catch { /* ignore */ }
        subscribe().catch(() => {})
      }
      window.addEventListener('pointerup', onTap, { once: true })
      removeTap = () => window.removeEventListener('pointerup', onTap)
    }

    start().catch(() => {})

    return () => {
      cancelled = true
      removeTap()
    }
  }, [installed, vapidKey, registerPush])

  return null
}
